/**
 * TableRow  component
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback } from "react";
import { getBaseClass } from "./baseClass";
import { useTableContext } from "./TableContext";
import { isFunction } from "@adapptio/lib-shared";

export interface ITableRowProps {
	id: string;
	className?: string;
	selectable?: boolean;
	onClick?: () => void;
}

export const TableRow: React.FC<ITableRowProps> = ({ children, className, id, selectable, onClick }) => {
	const { rowSelectable, onRowClick, selectedRows } = useTableContext();

	const selectAllowed = rowSelectable && selectable;

	const baseClass = getBaseClass("row", {
		selectable: selectAllowed || isFunction(onClick),
		selected: selectedRows?.includes(id)
	});

	const handleOnClick = useCallback(() => {
		if (selectAllowed) {
			if (isFunction(onRowClick)) {
				onRowClick(id);
			}
		}

		if (isFunction(onClick)) {
			onClick();
		}
	}, [ id, onRowClick, onClick, selectAllowed ]);

	if (className) {
		baseClass.add(className);
	}

	return (
		<tr className={baseClass.toClassName()} onClick={handleOnClick}>
			{children}
		</tr>
	);
};
